(() => {
  if (window.JustNoiseLayouts) return;
  const instances = new Map();
  class NativeLayout {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.sliders = [];
      this.timers = [];
      const listen = (node, event, handler, options = {}) => node.addEventListener(event, handler, { ...options, signal: this.abort.signal });
      this.listen = listen;
      root.querySelectorAll('.jnl-accordion__title').forEach(title => {
        title.setAttribute('role', 'button');
        title.tabIndex = 0;
        const item = title.parentElement;
        const body = item.querySelector(':scope > .jnl-accordion__body');
        if (body) {
          body.id ||= item.id + '-body';
          title.setAttribute('aria-controls', body.id);
        }
        const update = () => {
          const open = item.classList.contains('jnl-item-active');
          title.classList.toggle('jnl-item-active', open);
          title.setAttribute('aria-expanded', String(open));
        };
        update();
        const toggle = () => {
          const open = !item.classList.contains('jnl-item-active');
          item.parentElement.querySelectorAll(':scope > .jnl-accordion__item').forEach(sibling => {
            sibling.classList.toggle('jnl-item-active', sibling === item && open);
            const header = sibling.querySelector(':scope > .jnl-accordion__title');
            header?.classList.toggle('jnl-item-active', sibling === item && open);
            header?.setAttribute('aria-expanded', String(sibling === item && open));
          });
        };
        listen(title, 'click', toggle);
        listen(title, 'keydown', event => {
          if (['Enter', ' '].includes(event.key)) { event.preventDefault(); toggle(); }
        });
      });
      root.querySelectorAll('.tabs__wrapper').forEach(wrapper => {
        const tabs = [...wrapper.querySelectorAll('.tabs__nav')];
        const panels = [...wrapper.querySelectorAll(':scope > .core__group--items > .tab__item')];
        tabs[0]?.parentElement.setAttribute('role', 'tablist');
        const select = index => {
          tabs.forEach((tab, i) => {
            tab.classList.toggle('jnl-item-active', i === index);
            tab.setAttribute('aria-selected', String(i === index));
            tab.tabIndex = i === index ? 0 : -1;
          });
          panels.forEach((panel, i) => {
            panel.hidden = i !== index;
            panel.classList.toggle('jnl-item-active', i === index);
          });
        };
        tabs.forEach((tab, index) => {
          tab.setAttribute('role', 'tab');
          if (panels[index]) { tab.setAttribute('aria-controls', panels[index].id); panels[index].setAttribute('role', 'tabpanel'); }
          listen(tab, 'click', () => select(index));
          listen(tab, 'keydown', event => {
            if (['Enter', ' '].includes(event.key)) { event.preventDefault(); select(index); }
            if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
              event.preventDefault();
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
              select(next); tabs[next].focus();
            }
          });
        });
        select(0);
      });
      root.querySelectorAll('.jnl-swiper-wrapper,.jnx-splide__list').forEach(wrapper => {
        if (wrapper.tagName !== 'CSS-SLIDER') return;
        const slider = wrapper;
        this.sliders.push(slider);
        const ready = () => {
          slider.closest('.jnl-slider--container')?.classList.add('jnl-swiper-initialized-wrapper');
          slider.closest('.jnl-slider')?.classList.add('jnl-swiper-initialized');
          const simple = slider.closest('.jnx-splide');
          simple?.classList.add('is-initialized', 'jnx-slider-ready');
          simple?.classList.remove('jnx-slider-hidden');
        };
        listen(slider, 'ready', ready);
        if (typeof slider.changeSlide === 'function') ready();
        const group = slider.closest('.jnl-group') || root;
        const pagination = group.querySelector('.jnl-swiper-pagination-position');
        const slides = [...slider.querySelectorAll('.jnl-swiper-slide')];
        const labels = slides.map((slide, index) => slide.dataset.slideLabel || slide.querySelector('h1,h2,h3,h4,h5,h6')?.textContent.trim() || 'Slide ' + (index + 1));
        const bullets = [];
        const go = index => {
          slider.changeSlide?.(index);
          sync(index);
        };
        const sync = index => {
          slides.forEach((slide, i) => slide.classList.toggle('jnl-swiper-slide-active', i === index));
          bullets.forEach((bullet, i) => {
            bullet.classList.toggle('jnl-swiper-pagination-bullet-active', i === index);
            bullet.setAttribute('aria-selected', String(i === index));
            bullet.tabIndex = i === index ? 0 : -1;
          });
        };
        pagination?.replaceChildren();
        pagination?.classList.add('jnl-swiper-pagination-bullets');
        if (pagination) slides.forEach((slide, index) => {
          const bullet = document.createElement('span');
          bullet.className = 'jnl-swiper-pagination-bullet';
          bullet.setAttribute('role', 'tab');
          bullet.setAttribute('aria-label', labels[index]);
          bullet.dataset.label = labels[index];
          bullet.tabIndex = 0;
          if (root.dataset.nativeLayout !== 'zap-app') bullet.innerHTML = '<span class="visually-hidden"></span>';
          if (bullet.firstChild) bullet.firstChild.textContent = labels[index];
          pagination.append(bullet);
          bullets.push(bullet);
          listen(bullet, 'click', () => go(index));
          listen(bullet, 'keydown', event => {
            if (['Enter', ' '].includes(event.key)) { event.preventDefault(); go(index); }
            if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
              event.preventDefault();
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + slides.length) % slides.length;
              go(next); bullets[next].focus();
            }
          });
        });
        pagination?.setAttribute('role', 'tablist');
        group.querySelectorAll('.jnl-swiper-button').forEach(button => {
          const previous = button.classList.contains('jnl-swiper-button-prev');
          button.setAttribute('aria-label', previous ? 'Previous slide' : 'Next slide');
          listen(button, 'click', () => slider.changeSlide?.(previous ? 'prev' : 'next'));
        });
        listen(slider, 'change', () => sync(slider.index || 0));
        sync(0);
        const interval = { 'zap-app': 3000, 'zap-results': 4000 }[root.dataset.nativeLayout];
        if (interval && !this.motion.matches) {
          this.timers.push(setInterval(() => { if (!this.paused && !this.editorSelected && !this.motion.matches && root.getBoundingClientRect().bottom > 0 && root.getBoundingClientRect().top < innerHeight) slider.changeSlide?.((slider.index || 0) + 1 < slides.length ? 'next' : 0); }, interval));
        }
      });
      if (root.dataset.nativeLayout === 'landing-modes') {
        const content = root.querySelector('.slider-content css-slider');
        const navigation = root.querySelector('.slider-pagination css-slider');
        if (content && navigation) {
          const labels = [...navigation.querySelectorAll('.jnl-swiper-slide')];
          labels.forEach((label, index) => {
            label.setAttribute('role', 'button'); label.tabIndex = 0;
            label.setAttribute('aria-label', label.textContent.trim());
            const select = () => content.changeSlide?.(index);
            listen(label, 'click', select);
            listen(label, 'keydown', event => { if (['Enter', ' '].includes(event.key)) { event.preventDefault(); select(); } });
          });
          listen(content, 'change', () => {
            labels.forEach((label, index) => label.classList.toggle('jn-mode-selected', index === content.index));
            navigation.changeSlide?.(innerWidth <= 767 ? Math.max(0, content.index - 1) : 0);
          });
          labels[0]?.classList.add('jn-mode-selected');
          if (!this.motion.matches) this.timers.push(setInterval(() => {
            if (!this.paused && !this.editorSelected && !this.motion.matches && root.getBoundingClientRect().bottom > 0 && root.getBoundingClientRect().top < innerHeight) content.changeSlide?.((content.index || 0) + 1 < labels.length ? 'next' : 0);
          }, 3000));
        }
      }
      root.querySelectorAll('.jnl-video_overlay-icon').forEach(button => {
        button.setAttribute('aria-label', 'Play video');
        const container = button.closest('.jnl-element_video-iframe');
        const video = container?.querySelector('video');
        if (!video) return;
        listen(button, 'click', () => { container.classList.add('jn-video-playing'); video.play().catch(() => container.classList.remove('jn-video-playing')); });
        listen(video, 'pause', () => container.classList.remove('jn-video-playing'));
        listen(video, 'ended', () => container.classList.remove('jn-video-playing'));
      });
      root.querySelectorAll('.jnl-elements__lightbox-button-image').forEach(button => {
        button.setAttribute('aria-label', 'Play film');
        const video = button.closest('.jnl-element__lightbox-container')?.querySelector('video');
        if (!video) return;
        listen(button, 'click', () => {
          const dialog = document.createElement('dialog');
          dialog.className = 'jn-layout-film';
          const close = document.createElement('button');
          close.type = 'button'; close.className = 'jn-film-close'; close.setAttribute('aria-label', 'Close video'); close.textContent = '×';
          const player = video.cloneNode(true);
          dialog.append(close, player); document.body.append(dialog);
          listen(close, 'click', () => dialog.close());
          listen(dialog, 'close', () => { player.pause(); dialog.remove(); button.focus(); });
          dialog.showModal(); player.play().catch(() => {});
          this.dialog = dialog;
        });
      });
      root.querySelectorAll('video[autoplay]').forEach(video => {
        video.muted = true;
        if (this.motion.matches) { video.pause(); video.removeAttribute('autoplay'); }
      });
      const reveal = root.querySelector('.animated-section');
      if (reveal) {
        const nodes = [...reveal.querySelectorAll('.jnl-block .jnl-db,.jnl-block .text-content')].filter(node => !node.parentElement.closest('.jnl-db'));
        nodes.forEach(node => {
          const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
          const textNodes = [];
          while (walker.nextNode()) if (!walker.currentNode.parentElement.closest('.reveal-word')) textNodes.push(walker.currentNode);
          textNodes.forEach(text => {
            const fragment = document.createDocumentFragment();
            text.textContent.split(/(\s+)/).forEach(word => {
              if (!word.trim()) fragment.append(document.createTextNode(word));
              else { const span = document.createElement('span'); span.className = 'reveal-word'; span.textContent = word; fragment.append(span); }
            });
            text.replaceWith(fragment);
          });
        });
        const words = [...reveal.querySelectorAll('.reveal-word')];
        const update = () => {
          const mobile = innerWidth <= 767;
          const progress = this.motion.matches ? 1 : Math.max(0, Math.min(1, ((mobile ? .72 : .68) * innerHeight - reveal.getBoundingClientRect().top) / (words.length * (mobile ? 6.5 : 6))));
          words.forEach((word, index) => word.classList.toggle('is-active', index < Math.floor(progress * words.length)));
        };
        let frame;
        const schedule = () => { if (!frame) frame = requestAnimationFrame(() => { frame = null; update(); }); };
        this.cancelFrame = () => cancelAnimationFrame(frame);
        listen(window, 'scroll', schedule, { passive: true }); listen(window, 'resize', schedule); listen(this.motion, 'change', update); update();
      }
      listen(root, 'mouseenter', () => { this.paused = true; });
      listen(root, 'mouseleave', () => { this.paused = false; });
      listen(root, 'focusin', () => { this.paused = true; });
      listen(root, 'focusout', () => { this.paused = false; });
    }
    selectBlock(id) {
      const block = [...this.root.querySelectorAll('[data-native-block]')].find(node => node.dataset.nativeBlock === id);
      if (!block) return;
      const panel = block.closest('.tab__item');
      if (panel?.hidden) this.root.querySelector('[aria-controls="' + panel.id + '"]')?.click();
      const slider = block.closest('css-slider');
      if (slider) { slider.changeSlide?.([...slider.querySelectorAll('.jnl-swiper-slide')].indexOf(block)); this.editorSelected = true; }
    }
    destroy() {
      this.abort.abort(); this.timers.forEach(clearInterval); this.cancelFrame?.();
      this.root.querySelectorAll('video').forEach(video => video.pause());
      this.dialog?.close(); this.dialog?.remove();
    }
  }
  const mount = target => {
    const roots = [...target.querySelectorAll('[data-native-layout]')];
    if (target.matches?.('[data-native-layout]')) roots.unshift(target);
    roots.forEach(root => { if (!instances.has(root)) instances.set(root, new NativeLayout(root)); });
  };
  window.JustNoiseLayouts = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => { if (event.target.contains(root)) { instance.destroy(); instances.delete(root); } });
  });
  document.addEventListener('shopify:block:select', event => instances.forEach(instance => instance.selectBlock(event.detail.blockId)));
  document.addEventListener('shopify:section:select', event => instances.forEach((instance, root) => { if (event.target.contains(root)) instance.editorSelected = true; }));
  const releaseSelection = event => instances.forEach((instance, root) => { if (event.target.contains(root)) instance.editorSelected = false; });
  document.addEventListener('shopify:section:deselect', releaseSelection);
  document.addEventListener('shopify:block:deselect', releaseSelection);
})();
